import { motion } from 'framer-motion';
import { UserCheck, Building, Link2, CheckCircle2, Shield, FileCheck } from 'lucide-react';

const orgProfile = {
  'Org ID': 'ORG-7781',
  'Name': 'Al Noor Healthcare Group',
  'Owner EID': '784-1234-5678901-1',
  'Owner': 'Ahmed Al Rashid',
  'Status': 'Active',
};

const facilityProfile = {
  'Facility': 'Al Noor Medical Center',
  'License': 'LIC-2024-00892',
  'Type': 'Hospital',
  'Sub Type': 'General Hospital',
  'Address': 'Sheikh Zayed Road, Dubai',
  'Status': 'Active',
};

const payerFacility = {
  'Facility': 'ADNIC Claims Center',
  'License': 'LIC-2024-03201',
  'Type': 'Insurance',
  'Sub Type': 'Claims Processing',
  'Address': 'Hamdan Street, Abu Dhabi',
  'Status': 'Active',
};

function ProfileCard({ title, data, icon: Icon, delay, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white rounded-2xl border border-black/5 shadow-xl overflow-hidden"
    >
      <div className={`px-5 py-3 ${accent} flex items-center gap-2.5`}>
        <Icon size={18} className="text-white" />
        <h3 className="font-heading font-bold text-sm text-white">{title}</h3>
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: delay + 0.8 }}
          className="ml-auto"
        >
          <CheckCircle2 size={18} className="text-white/80" />
        </motion.div>
      </div>
      <div className="p-5 space-y-2">
        {Object.entries(data).map(([key, value], i) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: delay + 0.3 + i * 0.08 }}
            className="flex items-center justify-between py-1.5"
          >
            <span className="text-[13px] font-body text-[#737373]">{key}</span>
            <span className={`text-[13px] font-body font-medium ${key === 'Status' ? 'px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-xs' : 'text-[#0A0A0A]'}`}>
              {value}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

function LinkRow({ leftIcon: LIcon, leftName, leftSub, rightIcon: RIcon, rightName, rightSub, delay, lineColor, iconColor }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="flex items-center justify-center gap-5"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <LIcon size={20} className="text-[#0A0A0A] flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-heading font-bold text-[13px] text-[#0A0A0A] truncate">{leftName}</p>
          <p className="text-[11px] font-body text-[#737373] truncate">{leftSub}</p>
        </div>
      </div>
      <motion.div
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ delay: delay + 0.4, duration: 0.6 }}
        className="flex items-center gap-1.5 origin-left flex-shrink-0"
      >
        <div className={`w-16 h-[2px] bg-gradient-to-r from-[#0A0A0A] ${lineColor}`} />
        <Link2 size={16} className={iconColor} />
        <div className={`w-16 h-[2px] ${lineColor.replace('from-[#0A0A0A] ', '')}`} />
      </motion.div>
      <div className="flex items-center gap-2.5 min-w-0">
        <RIcon size={20} className={`${iconColor} flex-shrink-0`} />
        <div className="min-w-0">
          <p className="font-heading font-bold text-[13px] text-[#0A0A0A] truncate">{rightName}</p>
          <p className="text-[11px] font-body text-[#737373] truncate">{rightSub}</p>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: delay + 1.0 }}
        className="flex-shrink-0"
      >
        <CheckCircle2 size={20} className="text-emerald-500" />
      </motion.div>
    </motion.div>
  );
}

export default function ProfileBuildingScene() {
  return (
    <div data-testid="scene-profile-building" className="flex flex-col items-center justify-center h-full px-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full max-w-6xl"
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-medium bg-teal-600/10 text-teal-700 mb-4">
            <UserCheck size={14} /> Step 2
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-[#0A0A0A]">
            Build Profiles
          </h2>
          <p className="mt-3 font-body text-lg text-[#737373]">
            Organization, facility and payer profiles are created in Shafafiya
          </p>
        </motion.div>

        {/* Provider: Org + Facility */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <p className="text-[11px] font-body font-medium text-[#737373] uppercase tracking-wider mb-2.5 ml-1">Healthcare Provider</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <ProfileCard title="Organization Profile" data={orgProfile} icon={Building} delay={0.3} accent="bg-[#0A0A0A]" />
            <ProfileCard title="Facility Profile" data={facilityProfile} icon={FileCheck} delay={0.5} accent="bg-teal-600" />
          </div>
        </motion.div>

        {/* Payer: Facility only (no org, no separate payer profile) */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
          <p className="text-[11px] font-body font-medium text-[#737373] uppercase tracking-wider mb-2.5 ml-1">Payer / Insurance <span className="normal-case text-[#737373]/60">(facility only, no organization)</span></p>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-5 mb-5 flex justify-center">
            <div className="mx-auto w-full max-w-md">
              <ProfileCard title="ADNIC — Facility Profile" data={payerFacility} icon={FileCheck} delay={0.9} accent="bg-amber-500" />
            </div>
          </div>
        </motion.div>

        {/* Linking section */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.5 }}
          className="bg-white rounded-2xl border border-black/5 shadow-xl p-5 space-y-4"
        >
          <p className="text-xs font-body font-medium text-[#737373] text-center uppercase tracking-wider">Facility Linking</p>

          <LinkRow
            leftIcon={Building}
            leftName="Al Noor Healthcare Group"
            leftSub="ORG-7781"
            rightIcon={FileCheck}
            rightName="Al Noor Medical Center"
            rightSub="LIC-2024-00892"
            delay={1.6}
            lineColor="to-teal-600"
            iconColor="text-teal-600"
          />

          <LinkRow
            leftIcon={Shield}
            leftName="ADNIC"
            leftSub="PAY-4420 (Insurance Company)"
            rightIcon={FileCheck}
            rightName="ADNIC Claims Center"
            rightSub="LIC-2024-03201"
            delay={2.0}
            lineColor="to-amber-500"
            iconColor="text-amber-600"
          />

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3.0 }}
            className="text-center text-[12px] font-body text-[#737373]"
          >
            Org facility linked under organization &bull; Payer facility linked directly (no organization)
          </motion.p>
        </motion.div>
      </motion.div>
    </div>
  );
}
